import { useState, useEffect, useRef, forwardRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Input } from "@/components/ui/input";
import { UserPlus, X } from "lucide-react";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { cn } from "@/lib/utils";

interface MemberOption {
  id: string;
  full_name: string;
}

interface Props {
  value: string;
  onChange: (val: string) => void;
  onMemberSelect?: (member: MemberOption | null) => void;
  onCreateMember?: (name: string) => Promise<MemberOption | null>;
  placeholder?: string;
  className?: string;
  appendOnSelect?: boolean;
  // Render selected names as removable pills inside the input instead of plain text
  chips?: boolean;
  options?: MemberOption[];
  loading?: boolean;
  creating?: boolean;
}

const mergeSelectedName = (currentValue: string, selectedName: string) => {
  const cleanedCurrent = currentValue.trim();
  const cleanedSelected = selectedName.trim();
  if (!cleanedCurrent) return cleanedSelected;

  const existing = cleanedCurrent
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean);

  const hasCommaList = cleanedCurrent.includes(",");
  const endsWithComma = /,\s*$/.test(currentValue);

  if (hasCommaList) {
    if (endsWithComma) {
      existing.push(cleanedSelected);
    } else if (existing.length > 0) {
      existing[existing.length - 1] = cleanedSelected;
    }
  } else {
    if (existing[0].toLowerCase() === cleanedSelected.toLowerCase()) return existing[0];
    existing.push(cleanedSelected);
  }

  const deduped: string[] = [];
  const seen = new Set<string>();
  existing.forEach((name) => {
    const key = name.toLowerCase();
    if (!seen.has(key)) {
      seen.add(key);
      deduped.push(name);
    }
  });

  return deduped.join(", ");
};

const getInitials = (name: string) =>
  name
    .split(" ")
    .filter(Boolean)
    .map((part) => part[0]?.toUpperCase())
    .join("")
    .slice(0, 2) || "--";

const MemberAutocomplete = forwardRef<HTMLInputElement, Props>(
  ({ value, onChange, onMemberSelect, onCreateMember, placeholder = "Member name", className, appendOnSelect = false, chips = false, options, loading, creating = false }, ref) => {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState(value);
    const [typing, setTyping] = useState("");
    const containerRef = useRef<HTMLDivElement>(null);
    const chipsInputRef = useRef<HTMLInputElement | null>(null);

    const { data: queriedMembers = [], isLoading } = useQuery({
      queryKey: ["members_list_full"],
      queryFn: async () => {
        const { data, error } = await supabase
          .from("members")
          .select("id, full_name")
          .order("full_name");
        if (error) throw error;
        return data as MemberOption[];
      },
      enabled: !options,
    });

    const members = options || queriedMembers;
    const queryLoading = loading ?? isLoading;

    useEffect(() => { setQuery(value); }, [value]);

    // Close dropdown when clicking outside
    useEffect(() => {
      const handler = (e: MouseEvent) => {
        if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
          setOpen(false);
        }
      };
      document.addEventListener("mousedown", handler);
      return () => document.removeEventListener("mousedown", handler);
    }, []);

    const selectedNames = chips
      ? value.split(",").map((part) => part.trim()).filter(Boolean)
      : [];

    const addName = (name: string) => {
      const cleaned = name.trim();
      if (!cleaned) return;
      const seen = new Set(selectedNames.map((n) => n.toLowerCase()));
      const next = seen.has(cleaned.toLowerCase()) ? selectedNames : [...selectedNames, cleaned];
      onChange(next.join(", "));
      setTyping("");
    };

    const removeName = (name: string) => {
      onChange(selectedNames.filter((n) => n !== name).join(", "));
    };

    const activeQuerySegment = chips
      ? typing.trim()
      : query.split(",").pop()?.trim() || "";

    const selectedKeys = new Set(selectedNames.map((n) => n.toLowerCase()));
    const filtered = (activeQuerySegment
      ? members.filter((m) => m.full_name.toLowerCase().includes(activeQuerySegment.toLowerCase()))
      : members
    )
      .filter((m) => !chips || !selectedKeys.has(m.full_name.trim().toLowerCase()))
      .slice(0, 8);

    const trimmedQuery = (chips ? typing : query).trim();
    const hasExactMatch =
      trimmedQuery.length > 0 &&
      members.some((m) => m.full_name.trim().toLowerCase() === trimmedQuery.toLowerCase());

    const showDropdown = open && (queryLoading || filtered.length > 0 || (!!onCreateMember && trimmedQuery && !hasExactMatch));

    return (
      <div ref={containerRef} className={cn("relative", chips && "min-w-0 flex-1")}>
        {chips ? (
          <div
            className={cn(
              "flex min-h-9 w-full cursor-text flex-wrap items-center gap-1 rounded-md border border-input bg-background px-2 py-1",
              className,
            )}
            onClick={() => chipsInputRef.current?.focus()}
          >
            {selectedNames.map((name) => (
              <span
                key={name}
                className="flex items-center gap-1 rounded-full bg-secondary py-0.5 pl-0.5 pr-1 text-xs text-foreground"
              >
                <Avatar className="h-5 w-5 flex-shrink-0">
                  <AvatarFallback className="bg-primary/10 text-[9px] font-semibold text-primary">
                    {getInitials(name)}
                  </AvatarFallback>
                </Avatar>
                <span className="max-w-[140px] truncate">{name}</span>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); removeName(name); }}
                  className="rounded-full p-0.5 text-muted-foreground transition-colors hover:bg-muted-foreground/20 hover:text-foreground"
                  aria-label={`Remove ${name}`}
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            ))}
            <input
              ref={(node) => {
                chipsInputRef.current = node;
                if (typeof ref === "function") ref(node);
                else if (ref) ref.current = node;
              }}
              value={typing}
              onChange={(e) => {
                const v = e.target.value;
                if (v.includes(",")) {
                  const parts = v.split(",");
                  const rest = parts.pop() ?? "";
                  const seen = new Set(selectedNames.map((n) => n.toLowerCase()));
                  const next = [...selectedNames];
                  parts.map((p) => p.trim()).filter(Boolean).forEach((p) => {
                    if (!seen.has(p.toLowerCase())) { seen.add(p.toLowerCase()); next.push(p); }
                  });
                  onChange(next.join(", "));
                  setTyping(rest.trimStart());
                } else {
                  setTyping(v);
                }
                onMemberSelect?.(null);
                setOpen(true);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" && typing.trim()) {
                  e.preventDefault();
                  addName(typing);
                } else if (e.key === "Backspace" && !typing && selectedNames.length > 0) {
                  removeName(selectedNames[selectedNames.length - 1]);
                }
              }}
              onBlur={() => addName(typing)}
              onFocus={() => setOpen(true)}
              placeholder={selectedNames.length === 0 ? placeholder : ""}
              className={cn(
                "flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground",
                typing || selectedNames.length === 0 ? "min-w-[70px]" : "min-w-[8px]",
              )}
              autoComplete="off"
            />
          </div>
        ) : (
          <Input
            ref={ref}
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              onChange(e.target.value);
              onMemberSelect?.(null);
              setOpen(true);
            }}
            onFocus={() => setOpen(true)}
            placeholder={placeholder}
            className={className}
            autoComplete="off"
          />
        )}
        {showDropdown && (
          <div className="absolute z-[300] top-full left-0 right-0 mt-1 rounded-md border border-border bg-popover shadow-lg overflow-hidden">
            <div className="max-h-56 overflow-y-auto">
              {queryLoading ? (
                <div className="px-3 py-2 text-sm text-muted-foreground">Loading members...</div>
              ) : filtered.length > 0 ? (
                filtered.map((member) => (
                  <button
                    key={member.id}
                    type="button"
                    className="w-full flex items-center gap-2 text-left px-3 py-2 text-sm hover:bg-accent/10 transition-colors"
                    onMouseDown={(e) => {
                      // Prevent input blur before click registers
                      e.preventDefault();
                    }}
                    onClick={() => {
                      if (chips) {
                        addName(member.full_name);
                      } else {
                        const nextValue = appendOnSelect
                          ? mergeSelectedName(query, member.full_name)
                          : member.full_name;
                        onChange(nextValue);
                        setQuery(nextValue);
                      }
                      onMemberSelect?.(member);
                      setOpen(false);
                    }}
                  >
                    <Avatar className="w-6 h-6 flex-shrink-0">
                      <AvatarFallback className="bg-primary/10 text-primary text-[10px] font-semibold">
                        {getInitials(member.full_name)}
                      </AvatarFallback>
                    </Avatar>
                    {member.full_name}
                  </button>
                ))
              ) : (
                <div className="px-3 py-2 text-sm text-muted-foreground">No matching member found</div>
              )}
              {!queryLoading && !!onCreateMember && trimmedQuery && !hasExactMatch && (
                <button
                  type="button"
                  disabled={creating}
                  className="w-full flex items-center gap-2 text-left px-3 py-2 text-sm border-t border-border hover:bg-accent/10 transition-colors disabled:opacity-60"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={async () => {
                    const created = await onCreateMember(trimmedQuery);
                    if (!created) return;
                    if (chips) {
                      addName(created.full_name);
                    } else {
                      const nextValue = appendOnSelect
                        ? mergeSelectedName(query, created.full_name)
                        : created.full_name;
                      onChange(nextValue);
                      setQuery(nextValue);
                    }
                    onMemberSelect?.(created);
                    setOpen(false);
                  }}
                >
                  <UserPlus className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
                  {creating ? "Adding member..." : `+ Add "${trimmedQuery}" to People`}
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }
);

MemberAutocomplete.displayName = "MemberAutocomplete";
export default MemberAutocomplete;

